import { useCallback, useEffect, useState } from 'react'
import { isModiCafeQrGroup, type QrGroup, type QrLink } from '../data/qr'
import { qrGroupRepository } from '../data/qrRepository'

export type NewQrGroupInput = Omit<QrGroup, 'id' | 'order'>
export type NewQrLinkInput = Omit<QrLink, 'id' | 'order'>

const sortByOrder = <T extends { order: number }>(items: T[]) =>
  items.slice().sort((first, second) => first.order - second.order)

const cleanGroups = (items: QrGroup[]) => sortByOrder(items.filter(isModiCafeQrGroup))

export function useQrGroups() {
  const [groups, setGroups] = useState<QrGroup[]>([])

  useEffect(() => {
    let mounted = true

    qrGroupRepository.list().then((list) => {
      if (mounted) setGroups(cleanGroups(list))
    })

    const unsubscribe = qrGroupRepository.subscribe((list) => setGroups(cleanGroups(list)))

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  const addGroup = useCallback(async (input: NewQrGroupInput) => qrGroupRepository.create(input), [])

  const updateGroup = useCallback(
    async (id: string, patch: Partial<Omit<QrGroup, 'id'>>) => qrGroupRepository.update(id, patch),
    [],
  )

  const duplicateGroup = useCallback(async (id: string) => {
    const current = await qrGroupRepository.list()
    const source = cleanGroups(current).find((group) => group.id === id)
    if (!source) return

    return qrGroupRepository.create({
      label: { ...source.label, tr: `${source.label.tr} (Kopya)` },
      description: source.description,
      icon: source.icon,
      coverImage: source.coverImage,
      enabled: source.enabled,
      links: source.links.map((link) => ({ ...link, id: crypto.randomUUID() })),
    })
  }, [])

  const removeGroup = useCallback(async (id: string) => qrGroupRepository.remove(id), [])

  const reorderGroups = useCallback(
    async (orderedIds: string[]) => qrGroupRepository.reorder(orderedIds),
    [],
  )

  const addLink = useCallback(async (groupId: string, input: NewQrLinkInput) => {
    const current = await qrGroupRepository.list()
    const group = cleanGroups(current).find((item) => item.id === groupId)
    if (!group) return

    const nextOrder = group.links.length > 0 ? Math.max(...group.links.map((link) => link.order)) + 10 : 10
    const newLink: QrLink = { ...input, id: crypto.randomUUID(), order: nextOrder }
    return qrGroupRepository.update(groupId, { links: [...group.links, newLink] })
  }, [])

  const updateLink = useCallback(
    async (groupId: string, linkId: string, patch: Partial<Omit<QrLink, 'id'>>) => {
      const current = await qrGroupRepository.list()
      const group = cleanGroups(current).find((item) => item.id === groupId)
      if (!group) return

      const links = group.links.map((link) => (link.id === linkId ? { ...link, ...patch } : link))
      return qrGroupRepository.update(groupId, { links })
    },
    [],
  )

  const duplicateLink = useCallback(async (groupId: string, linkId: string) => {
    const current = await qrGroupRepository.list()
    const group = cleanGroups(current).find((item) => item.id === groupId)
    const source = group?.links.find((link) => link.id === linkId)
    if (!group || !source) return

    const nextOrder = Math.max(...group.links.map((link) => link.order)) + 10
    const newLink: QrLink = {
      ...source,
      id: crypto.randomUUID(),
      order: nextOrder,
      label: { ...source.label, tr: `${source.label.tr} (Kopya)` },
    }
    return qrGroupRepository.update(groupId, { links: [...group.links, newLink] })
  }, [])

  const removeLink = useCallback(async (groupId: string, linkId: string) => {
    const current = await qrGroupRepository.list()
    const group = cleanGroups(current).find((item) => item.id === groupId)
    if (!group) return

    return qrGroupRepository.update(groupId, {
      links: group.links.filter((link) => link.id !== linkId),
    })
  }, [])

  const reorderLinks = useCallback(async (groupId: string, orderedLinkIds: string[]) => {
    const current = await qrGroupRepository.list()
    const group = cleanGroups(current).find((item) => item.id === groupId)
    if (!group) return

    const byId = new Map(group.links.map((link) => [link.id, link]))
    const links = orderedLinkIds
      .map((id, index) => {
        const link = byId.get(id)
        return link ? { ...link, order: (index + 1) * 10 } : undefined
      })
      .filter((link): link is QrLink => Boolean(link))

    return qrGroupRepository.update(groupId, { links })
  }, [])

  return {
    groups,
    addGroup,
    updateGroup,
    duplicateGroup,
    removeGroup,
    reorderGroups,
    addLink,
    updateLink,
    duplicateLink,
    removeLink,
    reorderLinks,
  }
}
